import { useCallback, useEffect, useRef, useState } from "react";
import { X, Minus, Plus, Loader2, Check } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";

/**
 * PreferencesModal — Phase 10.
 *
 * A single lever for now: `default_logros_count` (0..50). Persists via
 * `POST /api/me/preferences`. Product decision (see PRD.md Phase 10):
 * changing this ONLY affects future days that don't have a
 * `daily_logros` doc yet — existing days keep the count they were
 * materialised with (zero destructive migration).
 *
 * The user's current value is read from `useAuth().user.preferences`.
 * On successful save we `refresh()` the auth context so any component
 * that depends on `user.preferences.default_logros_count` (e.g. the
 * agent when scheduling a brand-new day server-side) sees the new
 * value immediately.
 */
const MIN_N = 0;
const MAX_N = 50;

function clamp(n) {
  if (!Number.isFinite(n)) return 9;
  return Math.max(MIN_N, Math.min(MAX_N, Math.round(n)));
}

export default function PreferencesModal({ open, onClose }) {
  const { t } = useTranslation();
  const { user, refresh } = useAuth();
  const backdropDownRef = useRef(false);
  const initialN =
    user?.preferences?.default_logros_count != null
      ? clamp(user.preferences.default_logros_count)
      : 9;
  const [value, setValue] = useState(initialN);
  const [saving, setSaving] = useState(false);
  const [savedFlash, setSavedFlash] = useState(false);
  const [error, setError] = useState(null);

  // Whenever the modal opens, resync the local value to what's currently
  // in `user.preferences`. Prevents stale state if the user opened +
  // closed + reopened without saving.
  useEffect(() => {
    if (!open) return;
    setValue(initialN);
    setSaving(false);
    setSavedFlash(false);
    setError(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Escape closes.
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  const step = useCallback((delta) => {
    setValue((v) => clamp(v + delta));
    setSavedFlash(false);
  }, []);

  const onSliderChange = useCallback((e) => {
    setValue(clamp(parseInt(e.target.value, 10)));
    setSavedFlash(false);
  }, []);

  const onNumberChange = useCallback((e) => {
    const raw = e.target.value.replace(/[^\d]/g, "");
    const parsed = raw === "" ? 0 : parseInt(raw, 10);
    setValue(clamp(parsed));
    setSavedFlash(false);
  }, []);

  const handleSave = useCallback(async () => {
    if (saving) return;
    const next = clamp(value);
    setSaving(true);
    setError(null);
    try {
      await api.post("/me/preferences", { default_logros_count: next });
      // Refresh so any consumer of `user.preferences` picks up the new value.
      if (refresh) await refresh();
      setSavedFlash(true);
      // Post-Fase 10 fix: brief confirmation flash, then auto-close so the
      // user doesn't have to reach for the X after a save. If the modal is
      // already unmounted (fast toggle), guard the onClose call.
      setTimeout(() => {
        if (typeof onClose === "function") onClose();
      }, 700);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      setError(
        typeof detail === "string"
          ? detail
          : detail?.error || t("preferences.save_error", { defaultValue: "Error" }),
      );
    } finally {
      setSaving(false);
    }
  }, [value, saving, refresh, t, onClose]);

  if (!open) return null;

  const clamped = clamp(value);
  const dirty = clamped !== initialN;

  return (
    <div
      data-testid="preferences-overlay"
      className="fixed inset-0 z-[10000] flex items-center justify-center bg-black/65 backdrop-blur-sm animate-fade-in-up px-4"
      role="dialog"
      aria-modal="true"
      aria-label={t("preferences.title")}
      onMouseDown={(e) => {
        backdropDownRef.current = e.target === e.currentTarget;
      }}
      onMouseUp={(e) => {
        const ok = backdropDownRef.current && e.target === e.currentTarget;
        backdropDownRef.current = false;
        if (ok) onClose();
      }}
    >
      <div
        data-testid="preferences-modal"
        className="relative w-full max-w-[360px] rounded-[24px] px-6 pt-7 pb-5 bg-[rgba(10,14,26,0.96)] backdrop-blur-xl border border-[rgba(0,229,255,0.4)] shadow-[0_30px_80px_-10px_rgba(0,0,0,0.75),0_0_60px_-12px_rgba(0,229,255,0.45)]"
      >
        <button
          type="button"
          onClick={onClose}
          data-testid="preferences-close"
          aria-label={t("common.close")}
          className="absolute top-3 right-3 h-8 w-8 rounded-full flex items-center justify-center text-[#9CA3AF] hover:bg-white/[0.06] hover:text-[#E5E7EB]"
        >
          <X className="h-4 w-4" strokeWidth={2} />
        </button>

        <header className="mb-5 pr-6">
          <p className="text-[10px] uppercase tracking-[0.22em] text-[#00E5FF]/80">
            {t("preferences.subtitle")}
          </p>
          <h2
            className="mt-1 text-[18px] font-semibold text-[#E5E7EB]"
            data-testid="preferences-title"
          >
            {t("preferences.title")}
          </h2>
        </header>

        <section className="space-y-3">
          <label
            htmlFor="preferences-default-count"
            className="block text-[12.5px] font-medium text-[#E5E7EB]"
          >
            {t("preferences.default_logros_count_label")}
          </label>

          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={() => step(-1)}
              disabled={clamped <= MIN_N || saving}
              data-testid="preferences-stepper-minus"
              aria-label="−"
              className="h-9 w-9 shrink-0 rounded-full flex items-center justify-center bg-white/[0.03] border border-white/[0.1] text-[#9CA3AF] hover:text-[#00E5FF] hover:border-[rgba(0,229,255,0.4)] transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
            >
              <Minus className="h-4 w-4" strokeWidth={2} />
            </button>

            <input
              id="preferences-default-count"
              type="number"
              inputMode="numeric"
              min={MIN_N}
              max={MAX_N}
              value={clamped}
              onChange={onNumberChange}
              disabled={saving}
              data-testid="preferences-value-input"
              className="w-16 text-center font-mono font-semibold text-[22px] text-[#00E5FF] bg-white/[0.03] border border-[rgba(0,229,255,0.25)] rounded-xl py-1.5 focus:outline-none focus:border-[rgba(0,229,255,0.55)]"
            />

            <button
              type="button"
              onClick={() => step(1)}
              disabled={clamped >= MAX_N || saving}
              data-testid="preferences-stepper-plus"
              aria-label="+"
              className="h-9 w-9 shrink-0 rounded-full flex items-center justify-center bg-white/[0.03] border border-white/[0.1] text-[#9CA3AF] hover:text-[#00E5FF] hover:border-[rgba(0,229,255,0.4)] transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
            >
              <Plus className="h-4 w-4" strokeWidth={2} />
            </button>

            <div className="ml-auto text-right text-[11px] text-[#6B7280] font-mono">
              {MIN_N}–{MAX_N}
            </div>
          </div>

          <input
            type="range"
            min={MIN_N}
            max={MAX_N}
            step={1}
            value={clamped}
            onChange={onSliderChange}
            disabled={saving}
            data-testid="preferences-slider"
            aria-label={t("preferences.default_logros_count_label")}
            className="nx-preferences-slider w-full"
          />

          <p className="text-[11.5px] text-[#9CA3AF] leading-relaxed">
            {t("preferences.default_logros_count_help")}
          </p>
        </section>

        {error ? (
          <p
            className="mt-3 text-[11.5px] text-[#F87171]"
            data-testid="preferences-error"
          >
            {error}
          </p>
        ) : null}

        <button
          type="button"
          onClick={handleSave}
          disabled={saving || !dirty}
          data-testid="preferences-save"
          className={`
            mt-5 w-full h-11 rounded-full text-[12.5px] font-semibold
            inline-flex items-center justify-center gap-1.5
            transition-colors
            ${savedFlash
              ? "bg-[rgba(0,229,255,0.12)] border border-[rgba(0,229,255,0.55)] text-[#00E5FF]"
              : "bg-[rgba(0,229,255,0.16)] border border-[rgba(0,229,255,0.55)] text-[#00E5FF] hover:bg-[rgba(0,229,255,0.24)] shadow-[0_0_22px_rgba(0,229,255,0.3)]"}
            disabled:opacity-50 disabled:cursor-not-allowed disabled:shadow-none
          `}
        >
          {saving ? (
            <Loader2 className="h-4 w-4 animate-spin" strokeWidth={2} />
          ) : savedFlash ? (
            <>
              <Check className="h-4 w-4" strokeWidth={2.5} />
              <span data-testid="preferences-saved-flash">{t("preferences.saved")}</span>
            </>
          ) : (
            <span>{t("preferences.save")}</span>
          )}
        </button>
      </div>
    </div>
  );
}
